import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, CalendarClock } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrdenesTrabajoContext, type TecnicoAsignado } from '@/contexts/OrdenesTrabajoContext';
import { TecnicosMultiSelector } from '@/components/ordenes/TecnicosMultiSelector';
import { getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { tipoProximaLabel } from '@/lib/coberturaRevisiones';
import type { SistemaRevisionRow } from '@/hooks/useMantenimientosDashboard';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  sistemas: SistemaRevisionRow[];
  onCreada?: () => void;
}

const isoDate = (d?: Date) => (d ? format(d, 'yyyy-MM-dd') : '');

export const ProgramarRevisionDialog: React.FC<Props> = ({ open, onOpenChange, sistemas, onCreada }) => {
  const { createOrden } = useOrdenesTrabajoContext();
  const { toast } = useToast();
  const [titulo, setTitulo] = useState('');
  const [fecha, setFecha] = useState('');
  const [hora, setHora] = useState('');
  const [duracion, setDuracion] = useState('');
  const [prioridad, setPrioridad] = useState<'baja' | 'media' | 'alta' | 'urgente'>('media');
  const [notas, setNotas] = useState('');
  const [tecnicos, setTecnicos] = useState<TecnicoAsignado[]>([]);
  const [guardando, setGuardando] = useState(false);

  const principal = sistemas[0];
  const vencido = useMemo(
    () => sistemas.some(s => s.cobertura.estado === 'vencido'),
    [sistemas],
  );

  const descripcion = useMemo(() => {
    const lineas = sistemas.map(s => {
      const tipoRev = s.cobertura.proxima ? tipoProximaLabel(s.cobertura.proxima.tipo) : 'Revisión pendiente';
      const sede = [s.sedeNombre, s.sedePoblacion].filter(Boolean).join(' · ');
      return `• ${getSystemTypeLabel(s.tipo)}${sede ? ` (${sede})` : ''} — ${tipoRev}`;
    });
    return `Revisión de mantenimiento de los siguientes sistemas:\n${lineas.join('\n')}`;
  }, [sistemas]);

  useEffect(() => {
    if (!open || !principal) return;
    const tipoRev = principal.cobertura.proxima
      ? tipoProximaLabel(principal.cobertura.proxima.tipo)
      : 'Revisión';
    const sede = principal.sedeNombre ? ` · ${principal.sedeNombre}` : '';
    setTitulo(
      sistemas.length > 1
        ? `Revisiones de mantenimiento${sede}`
        : `${tipoRev} · ${getSystemTypeLabel(principal.tipo)}${sede}`,
    );
    setFecha(isoDate(principal.cobertura.proxima?.fecha));
    setHora('');
    setDuracion('');
    setNotas('');
    setTecnicos([]);
    setPrioridad(vencido ? 'alta' : 'media');
  }, [open, principal, sistemas.length, vencido]);

  const guardar = async () => {
    if (!principal || !titulo.trim()) return;
    setGuardando(true);
    try {
      const orden = await createOrden(
        {
          titulo: titulo.trim(),
          descripcion,
          cliente_id: principal.clienteId,
          sede_id: principal.sedeId ?? undefined,
          tipo_trabajo: 'mantenimiento',
          prioridad,
          estado: tecnicos.length > 0 ? 'asignada' : 'creada',
          tecnico_asignado_id: tecnicos.find(t => t.rol === 'principal')?.tecnico_id,
          fecha_limite: fecha || undefined,
          hora_ejecucion: hora || undefined,
          duracion_estimada: duracion || undefined,
          notas_admin: notas || undefined,
        } as any,
        tecnicos.length > 0 ? tecnicos : undefined,
      );

      const vinculos = sistemas.map(s => ({
        orden_trabajo_id: orden.id,
        sistema_id: s.sistemaId,
        tipo_revision: s.cobertura.proxima?.tipo ?? null,
      }));
      const { error } = await (supabase as any).from('orden_trabajo_sistemas').insert(vinculos);
      if (error) throw error;

      toast({ title: 'Revisión programada', description: `OT ${orden.numero_ot} creada correctamente.` });
      onOpenChange(false);
      onCreada?.();
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: 'No se pudo programar la revisión.', variant: 'destructive' });
    } finally {
      setGuardando(false);
    }
  };

  if (!principal) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" />
            Programar revisión
          </DialogTitle>
          <DialogDescription>
            {principal.clienteNombre}
            {principal.sedeNombre ? ` · ${principal.sedeNombre}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {sistemas.map(s => (
              <Badge key={s.sistemaId} variant="secondary">{getSystemTypeLabel(s.tipo)}</Badge>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Título</Label>
            <Input value={titulo} onChange={e => setTitulo(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fecha prevista</Label>
              <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hora</Label>
              <Input type="time" value={hora} onChange={e => setHora(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Duración estimada</Label>
              <Input placeholder="2h" value={duracion} onChange={e => setDuracion(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Prioridad</Label>
              <Select value={prioridad} onValueChange={(v: any) => setPrioridad(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="baja">Baja</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="urgente">Urgente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <TecnicosMultiSelector value={tecnicos} onChange={setTecnicos} />
          <p className="text-xs text-muted-foreground -mt-2">
            Si no asignas a nadie, la OT quedará en la bandeja para planificarla más tarde.
          </p>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea rows={3} value={notas} onChange={e => setNotas(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={guardando || !titulo.trim()}>
            {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Crear OT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
